// Compatibility wrapper for react-to-print v3.x
// This provides the old ReactToPrint component API using the new useReactToPrint hook
// Usage: Replace `import ReactToPrint from 'react-to-print'`
//        with `import ReactToPrint from '../../Shared/print/ReactToPrintCompat'`

import React, { useRef, useCallback } from 'react';
import { useReactToPrint } from 'react-to-print';

const ReactToPrint = ({ 
  trigger, 
  content, 
  onBeforeGetContent, 
  onAfterPrint, 
  pageStyle, 
  documentTitle, 
  removeAfterPrint,
  onBeforePrint,
  onPrintError,
  copyStyles = true,
  suppressErrors = false,
}) => {
  // Get the content ref from the content function
  const getContentRef = useCallback(() => {
    if (typeof content === 'function') {
      const result = content();
      // If it's a ref object, return it directly
      if (result && typeof result === 'object' && 'current' in result) {
        return result;
      }
      // If it's a DOM node, wrap it in a ref-like object
      return { current: result };
    }
    return { current: null };
  }, [content]);

  const handlePrint = useReactToPrint({
    contentRef: getContentRef(),
    onBeforeGetContent,
    onAfterPrint,
    pageStyle,
    documentTitle,
    removeAfterPrint,
    onBeforePrint,
    onPrintError,
    copyStyles,
    suppressErrors,
  });

  const attachHandler = (element) => {
    if (!React.isValidElement(element)) return element;
    const existingOnClick = element.props?.onClick;
    const handleClick = (event) => {
      if (typeof existingOnClick === 'function') {
        existingOnClick(event);
      }
      handlePrint();
    };
    return React.cloneElement(element, { onClick: handleClick });
  };

  if (typeof trigger === 'function') {
    const triggerElement = trigger(handlePrint);
    return attachHandler(triggerElement);
  }

  return attachHandler(trigger);
};

export default ReactToPrint;
